/* eslint-disable react/jsx-closing-bracket-location */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { FC, ReactElement, useEffect, useContext, useState } from 'react';
import { CompetitionsContext, PrefferencesContext } from '../../context/context';
import { getMatches } from '../../services/http/httpService';
import { IIceHockeyMatch, IIceHockeyMatchScore } from '../../interfaces/interfaces';
import { REFRESH_INTERVAL, WIDGET_WIDTH } from '../../config/config';
import TableLoader from '../loader/loader';
import { trans, getRealGameTimestamp, getParameterByName } from '../../helpers/helperFunctions';

const Matches: FC = (): ReactElement | null => {
  const { selectedCompetition, matchDay } = useContext(CompetitionsContext);

  const { language, widgetWidth } = useContext(PrefferencesContext);
  const { activeSeasonId, competitionId } = selectedCompetition;
  const [loading, setLoading] = useState<boolean>(true);
  const [matches, setMatches] = useState<IIceHockeyMatch[]>([]);

  // Fetches standings when a competition is selected manually or on load
  useEffect(() => {
    if (!activeSeasonId || matchDay === null) return undefined;
    setLoading(true);
    getMatches(competitionId, activeSeasonId, matchDay, language)
      .then((response) => {
        setLoading(false);
        setMatches(response);
      })
      .catch((e) => {
        console.log('Error fetching standings');
        setLoading(false);
      });

    // Register interval to re-fetch standings
    const standingsInterval = setInterval(() => {
      if (!activeSeasonId || matchDay === null) return;
      getMatches(competitionId, activeSeasonId, matchDay, language)
        .then((response) => {
          setMatches(response);
        })
        .catch((e) => {
          console.log('Error fetching standings');
        });
    }, REFRESH_INTERVAL);

    return () => clearInterval(standingsInterval);
  }, [activeSeasonId, competitionId, matchDay]);
  return createMatches(matches, loading, widgetWidth);
};

function createMatches(
  matches: IIceHockeyMatch[],
  loading: boolean,
  widgetWidth: number
): ReactElement {
  // + operator is before Date due typescript warning issue https://github.com/microsoft/TypeScript/issues/5710
  // basiclly convert to number
  matches.sort((a, b) => +new Date(a.date) - +new Date(b.date));
  const collapsed = getParameterByName('collapsed');
  const [isCollapsed, setIsCollapsed] = useState(collapsed === 'true');

  return (
    <div className="matches-container">
      <i
        aria-hidden="true"
        className="material-icons collapse-icon"
        onClick={() => setIsCollapsed(!isCollapsed)}>
        {isCollapsed ? 'unfold_more' : 'unfold_less'}
      </i>{' '}
      <p className="table-title">{trans('results')}</p>
      {loading ? (
        <TableLoader viewBox="10 10 1200 720" />
      ) : (
        !isCollapsed && (
          <table className="matches-table">
            <tbody>
              {matches.map((row, index) => {
                const {
                  home,
                  homeMicro,
                  away,
                  awayMicro,
                  teamLogoUrlHome,
                  teamLogoUrlAway,
                  date,
                  score,
                  postponed
                } = row;
                const homeName = widgetWidth <= WIDGET_WIDTH ? homeMicro : home;
                const awayName = widgetWidth <= WIDGET_WIDTH ? awayMicro : away;
                return (
                  <tr key={`${home}${away}${index}${Math.random()}`}>
                    <td>
                      <img src={teamLogoUrlHome} alt="Team logo" />
                    </td>
                    <td>{homeName}</td>
                    {createScoreRow(getRealGameTimestamp(date), score, postponed)}
                    <td>{awayName}</td>
                    <td>
                      <img src={teamLogoUrlAway} alt="Team logo" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

function createScoreRow(gameDate: number, score: IIceHockeyMatchScore, postponed: boolean) {
  if (postponed) return <td>{trans('postponed')}</td>;
  const { fullTime, period1, period2, period3, overtime } = score;
  const notStarted = gameDate > Date.now();

  if (notStarted) {
    const newDate = new Date(gameDate);
    const date = newDate.getDate();
    const month = newDate.getMonth() + 1;
    const year = newDate.getFullYear();
    const hour = newDate.getHours();
    const minutes = newDate.getMinutes() !== 0 ? newDate.getMinutes() : '00';
    const displayTime = `${date}.${month}.${year} - ${hour}:${minutes}`;
    return <td>{displayTime}</td>;
  }

  return (
    <td>
      <span className="full-time-score">
        <b>
          {fullTime?.home ? fullTime?.home : '-'}:{fullTime?.away ? fullTime?.away : '-'}
        </b>
      </span>
      <div>
        (
        <span>
          {period1.home ? period1.home : '-'}:{period1.away ? period1.away : '-'},
        </span>
        <span>
          {period2.home ? period2.home : '-'}:{period2.away ? period2.away : '-'},
        </span>
        <span>
          {period3.home ? period3.home : '-'}:{period3.away ? period3.away : '-'}
        </span>
        {overtime && (
          <span>
            ,{overtime ? overtime.home : '-'}:{overtime ? overtime.away : '-'}
          </span>
        )}
        )
      </div>
    </td>
  );
}

export default Matches;
